import {
  buildCollection,
  buildEntityCallbacks,
  buildProperties,
  buildProperty,
} from "@firecms/core";
import { buildLocalizedStringProperty, type LocalizedString } from "../localization";
import { codeProperty } from "../properties/common";
import {
  getContentBasePath,
  type ContentScopeId,
} from "../workspace/collectionIds";
import {
  onPreSaveContentTimestamps,
  contentTimestampsProperties,
  type ContentTimestamps,
} from "./contentTimestamps";
import { buildContentImportActions } from "../contentImport/buildContentImportActions";
import type { Locale } from "../localization/constants";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";

export const UnlockMode = {
  UNLOCK_MODE_FREE: "UNLOCK_MODE_FREE",
  UNLOCK_MODE_BLOCKED: "UNLOCK_MODE_BLOCKED",
} as const;

export type UnlockMode = (typeof UnlockMode)[keyof typeof UnlockMode];

export type Unit = ContentTimestamps & {
  code: string;
  name: LocalizedString;
  unlockMode: UnlockMode;
  accessCode?: string;
};

const unlockModeEnumValues = (t: ReturnType<typeof getAdminT>) => ({
  [UnlockMode.UNLOCK_MODE_FREE]: t("eventContent.unlockModeFree"),
  [UnlockMode.UNLOCK_MODE_BLOCKED]: t("eventContent.unlockModeBlocked"),
});

export const buildUnitsPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/units`;

export const buildRelatedUnitsCollection = (id: ContentScopeId) =>
  buildCollection<Partial<Unit>>({
    name: "Units",
    singularName: "Unit",
    description:
      "Units from the content attached to this session. To edit them, open the learning path from the account menu.",
    path: buildUnitsPath(id),
    initialSort: ["code", "asc"],
    hideFromNavigation: true,
    id: "relatedUnits",
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    properties: {
      code: codeProperty,
      name: buildLocalizedStringProperty({
        name: "Name",
        dataType: "string",
        readOnly: true,
      }),
    },
  });

export const buildUnitCollection = (
  id: ContentScopeId,
  locale: Locale = getAdminUiLocale()
) => {
  const t = getAdminT(locale);

  return buildCollection<Unit>({
    name: "Units",
    singularName: "Unit",
    description:
      "Units segment a learning path—organize stories, questions, and levels so participants progress in a clear order.",
    path: buildUnitsPath(id),
    id: "units",
    icon: "School",
    initialSort: ["code", "asc"],
    group: "Learn",
    callbacks: buildEntityCallbacks<Unit>({
      onPreSave: onPreSaveContentTimestamps,
    }),
    Actions: buildContentImportActions("units"),
    properties: buildProperties<Unit>({
      code: codeProperty,
      name: buildLocalizedStringProperty({
        name: "Name",
        validation: { required: true },
        dataType: "string",
      }),
      unlockMode: buildProperty({
        name: t("eventContent.unlockMode"),
        description: t("eventContent.unlockModeDescription"),
        validation: { required: true },
        dataType: "string",
        enumValues: unlockModeEnumValues(t),
      }),
      ...contentTimestampsProperties,
    }),
  });
};
